import { useState } from 'react';

import { tagsApi, type ConditionTree, type CreateTagRequest } from '@/api/queries/workspace';
import { useConditionValueSuggestions } from '@/api/queries/conditionFields';
import {
  Button,
  ConditionBuilder,
  Field,
  Icon,
  Input,
  ModalRoot,
  IconButton,
} from '@/components/ui';
import { cn } from '@/utils/cn';

import { TAG_COLOR_PRESETS, TAG_RULE_FIELDS } from './TagFields';

interface NewTagModalProps {
  open: boolean;
  onClose: () => void;
}

export function NewTagModal({ open, onClose }: NewTagModalProps) {
  const create = tagsApi.useCreate();
  const [name, setName] = useState('');
  const [color, setColor] = useState(TAG_COLOR_PRESETS[0]);
  const [autoApply, setAutoApply] = useState(false);
  const [rule, setRule] = useState<ConditionTree>({ conjunction: 'and', conditions: [] });
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setName('');
    setColor(TAG_COLOR_PRESETS[0]);
    setAutoApply(false);
    setRule({ conjunction: 'and', conditions: [] });
    setError(null);
    onClose();
  };

  const submit = async () => {
    setError(null);
    if (!name.trim()) {
      setError('Tag name is required');
      return;
    }
    try {
      const namespace = name.includes(':') ? name.split(':')[0] : undefined;
      const body: CreateTagRequest = {
        name: name.trim(),
        color,
        namespace,
        autoRule: autoApply ? rule : undefined,
      };
      await create.mutateAsync(body);
      close();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create tag');
    }
  };

  return (
    <ModalRoot open={open} onClose={close} size="md" closeOnScrim>
      <div className="ds-modal-head">
        <h2>New tag</h2>
        <IconButton icon="x" label="Close" className="ds-modal-close" onClick={close} />
      </div>
      <div className="ds-modal-body">
        <div className="ds-form-row">
          <Field label="Tag name" required hint="Use a colon prefix to namespace, e.g. kategorie:lekar">
            <Input
              autoFocus
              value={name}
              placeholder="vip, kategorie:lekar, …"
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field label="Color">
            <div className="color-picker">
              {TAG_COLOR_PRESETS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Color ${c}`}
                  className={cn('swatch', c === color && 'active')}
                  style={{ background: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </Field>

          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--surface-2)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={autoApply}
              onChange={(e) => setAutoApply(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: 'var(--brand)' }}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text)' }}>
                Auto-apply to matching contacts
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                Re-evaluates whenever a contact changes
              </div>
            </div>
          </label>

          {autoApply && (
            <Field label="Auto-rule">
              <ConditionBuilder
                value={rule}
                onChange={setRule}
                fields={TAG_RULE_FIELDS}
                useValueSuggestions={useConditionValueSuggestions}
              />
            </Field>
          )}

          {error && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 12px',
                background: 'var(--red-soft)',
                color: 'var(--red)',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12.5,
              }}
            >
              <Icon name="alert" size={14} /> {error}
            </div>
          )}
        </div>
      </div>
      <div className="ds-modal-foot">
        <span style={{ flex: 1 }} />
        <Button variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={create.isPending}
          leftIcon={!create.isPending ? <Icon name="check" size={13} /> : undefined}
          onClick={() => void submit()}
        >
          Create tag
        </Button>
      </div>
    </ModalRoot>
  );
}
